/**
 * Admin reset-password: generate a random password, save via Cognito,
 * then show it once for copy. Closing the reveal step discards it from the UI.
 */
import React, { useState, useCallback, useEffect } from 'react';
import KeyRound from 'lucide-react/dist/esm/icons/key-round';
import Check from 'lucide-react/dist/esm/icons/check';
import Copy from 'lucide-react/dist/esm/icons/copy';
import X from 'lucide-react/dist/esm/icons/x';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import { adminSetPassword } from '../../../services/api/org';
import type { OrgUser } from '../../../services/api/org';
import { Button, Modal, Alert, Text, IconButton } from '../../../components/ui';

interface ResetPasswordModalProps {
  isOpen: boolean;
  user: OrgUser;
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = 'confirm' | 'revealed';

const SPECIALS = '!@#$%^&*';
const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS = '0123456789';
const ALPHABET = UPPER + LOWER + DIGITS + SPECIALS;

function pickChar(chars: string): string {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return chars[buf[0] % chars.length];
}

/** Cognito-compatible password: upper, lower, digit, special, length 16. */
export function generateRandomPassword(length = 16): string {
  const chars = [
    pickChar(UPPER),
    pickChar(LOWER),
    pickChar(DIGITS),
    pickChar(SPECIALS),
  ];
  for (let i = chars.length; i < length; i++) {
    chars.push(pickChar(ALPHABET));
  }
  for (let i = chars.length - 1; i > 0; i--) {
    const buf = new Uint32Array(1);
    crypto.getRandomValues(buf);
    const j = buf[0] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

const ResetPasswordModal: React.FC<ResetPasswordModalProps> = ({
  isOpen,
  user,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>('confirm');
  const [password, setPassword] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setStep('confirm');
    setPassword(null);
    setSubmitting(false);
    setError(null);
    setCopied(false);
  }, [isOpen, user.id]);

  const handleGenerate = useCallback(async () => {
    setSubmitting(true);
    setError(null);
    const next = generateRandomPassword();
    try {
      await adminSetPassword(user.id, next);
      setPassword(next);
      setStep('revealed');
      onSuccess?.();
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      setError(detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  }, [user.id, onSuccess]);

  const handleCopy = useCallback(() => {
    if (!password) return;
    void navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [password]);

  const handleClose = useCallback(() => {
    if (submitting) return;
    setPassword(null);
    onClose();
  }, [submitting, onClose]);

  const displayName = user.display_name || user.email.split('@')[0];

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Header>
        <div className="p-2 rounded-xl bg-brand/15">
          <KeyRound size={18} className="text-brand" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-content">Reset Password</h2>
          <p className="text-xs text-content-muted mt-0.5 truncate">{displayName}</p>
        </div>
        {!submitting && (
          <button
            type="button"
            onClick={handleClose}
            className="p-1.5 rounded-lg text-content-muted hover:text-content-secondary hover:bg-surface-primary cursor-pointer transition-colors border-none outline-none"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        )}
      </Modal.Header>

      {step === 'confirm' ? (
        <>
          <Modal.Body className="space-y-4">
            {error && <Alert variant="error">{error}</Alert>}
            <Text variant="body" className="text-content-secondary">
              Generate a new random password for <span className="text-content font-medium">{user.email}</span>?
              Their current password will stop working immediately.
            </Text>
            <Alert variant="warning">
              The new password is shown once after reset. Copy it before closing — it cannot be retrieved later.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="ghost" size="lg" type="button" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="primary" size="lg" loading={submitting} onClick={handleGenerate}>
              Generate & Reset
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <>
          <Modal.Body className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-400/10 border border-amber-400/20">
              <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
              <Text variant="caption" className="text-amber-400">
                Copy this password now. It will not be shown again after you close this dialog.
              </Text>
            </div>
            <div>
              <Text variant="label" className="mb-2">New password</Text>
              <div className="flex items-center gap-2 p-3 rounded-xl bg-surface-secondary border border-subtle">
                <code className="flex-1 min-w-0 text-sm font-mono text-content break-all select-all">
                  {password}
                </code>
                <IconButton
                  icon={copied ? <Check size={14} className="text-status-success" /> : <Copy size={14} />}
                  variant="ghost"
                  size="sm"
                  aria-label={copied ? 'Copied' : 'Copy password'}
                  onClick={handleCopy}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" size="lg" onClick={handleClose}>
              Done
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default ResetPasswordModal;
